/**
 * Canonical URL builders for content that lives under a section prefix (LIND-27).
 *
 * Clarity could not segment sessions because Solutions and news articles were
 * split between the site root and their section. Everything now nests under its
 * section, and the legacy root-level URLs are 301'd by the `redirects` block in
 * serve.json. Build hrefs with these helpers so internal links always point at
 * the canonical URL rather than through a redirect.
 */

/** Every Solution is a subpage of /warehousing-solutions. */
export function solutionHref(solution: { slug?: string } | string | undefined): string {
	const slug = typeof solution === 'string' ? solution : solution?.slug;
	return `/warehousing-solutions/${slug}`;
}

/** Every news article is a subpage of /news. */
export function newsHref(article: { slug?: string } | string | undefined): string {
	const slug = typeof article === 'string' ? article : article?.slug;
	return `/news/${slug}`;
}

/**
 * Solutions that have been retired. The Contentful entry still exists, but its
 * URL 301s to a replacement, so nothing should link to it or list it in the
 * sitemap — every inbound internal link would otherwise be a redirect hop.
 */
export const RETIRED_SOLUTION_SLUGS = ['fridge-storage'];

export function isRetiredSolution(slug: string | undefined): boolean {
	return !!slug && RETIRED_SOLUTION_SLUGS.includes(slug);
}

/** Drops retired Solutions from a list before it is rendered or indexed. */
export function liveSolutions<T extends { slug?: string }>(solutions: T[] | undefined): T[] {
	return (solutions ?? []).filter((solution) => !isRetiredSolution(solution.slug));
}
